import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import useIsMobile from '../../hooks/useIsMobile';
import SavedListNotificationsModal from './SavedListNotificationsModal';
import MyListsHub from './MyListsHub';
import SavedListsEmptyState from './SavedListsEmptyState';
import SavedListsFreeUpsell from './SavedListsFreeUpsell';
import SavedListsLapsedMessage from './SavedListsLapsedMessage';
import HubError from './HubError';
import ListDetail from '../../SavedLists/ListDetail';
import useSavedPlayerLists from './useSavedPlayerLists';
import { SavedListsAccessStatus } from './constants';
import { showPopupNew } from '../../../actions/popup-actions';
import { clearOpenSavedListRequest } from '../../../actions/saved-lists-actions';
import { PopupTypes } from '../../../utils/constants';
import styles from './savedListsTabContent.scss';

const HUB_TESTID = 'saved-lists-hub';

const HUB_ERROR_TESTID = `${HUB_TESTID}.error`;

const EMPTY_STATE_TESTID = 'saved-lists-empty';

const FREE_UPSELL_TESTID = 'saved-lists-upsell';

const LAPSED_TESTID = 'saved-lists-lapsed';

const SavedListsTabContent = ({ 'data-testid': dataTestId }) => {
    const [isNotificationsModalOpen, setIsNotificationsModalOpen] = useState(false);
    const [selectedListId, setSelectedListId] = useState(null);
    const dispatch = useDispatch();
    // Set when something outside this subtree asks to open a list (the hub Add-Player "View List"
    // toast). Consume it once to open List Detail, then clear so it doesn't re-fire on a later visit.
    const pendingOpenListId = useSelector((state) => state.savedLists.pendingOpenListId);
    const { isMobileWidth } = useIsMobile();
    const isDesktop = !isMobileWidth;
    const {
        accessStatus,
        totalPlayerCount,
        lists,
        total,
        totalPages,
        page,
        sortColumn,
        sortType,
        search,
        isLoading,
        isError,
        setSearch,
        setSort,
        setPage,
        deleteList,
        refetch,
    } = useSavedPlayerLists();

    const isPower = accessStatus === SavedListsAccessStatus.POWER;
    const actionClassName = `${styles.actionLink} ${isPower ? '' : styles.actionLinkDisabled}`;

    const handleAddNewList = () => {
        dispatch(showPopupNew({ popup: PopupTypes.CREATE_LIST_POPUP, onSuccess: refetch }));
    };

    const handleAddPlayer = () => {
        dispatch(showPopupNew({ popup: PopupTypes.ADD_PLAYER_TO_LIST_POPUP, onSuccess: refetch }));
    };

    useEffect(() => {
        if (pendingOpenListId === null || pendingOpenListId === undefined) {
            return;
        }
        setSelectedListId(pendingOpenListId);
        dispatch(clearOpenSavedListRequest());
    }, [pendingOpenListId, dispatch]);

    const renderBody = () => {
        if (accessStatus === null) {
            // Status not resolved yet: on the very first feed call surface the hub's own loading state
            // (column headers + row skeleton) — not a generic card — so the first-load skeleton matches
            // the loaded table with no layout shift. A failed first call shows an error+retry instead.
            // Never fall through to the non-Power branch, which would grey out the body and header for a
            // member whose status we simply haven't loaded.
            if (isError) {
                return (
                    <div className={styles.hubStatus}>
                        <HubError data-testid={HUB_ERROR_TESTID} onRetry={refetch} />
                    </div>
                );
            }
            return (
                <MyListsHub
                    data-testid={HUB_TESTID}
                    isLoading
                    onSearch={setSearch}
                    onSort={setSort}
                    onPageChange={setPage}
                    onDeleteList={deleteList}
                    onOpenList={setSelectedListId}
                    onRetry={refetch}
                />
            );
        }
        // The feed returns the member's default list even for Free (TMS-2150 page-1-open-to-all), but
        // the Free view is header-only — render the hub chrome with no rows/count for Free while Power
        // keeps the real feed data.
        const isFree = accessStatus === SavedListsAccessStatus.FREE;
        const hub = (
            <MyListsHub
                data-testid={HUB_TESTID}
                lists={isFree ? [] : lists}
                total={isFree ? 0 : total}
                totalPages={isFree ? 0 : totalPages}
                page={page}
                sortColumn={sortColumn}
                sortType={sortType}
                search={search}
                // Free renders chrome only (no rows/count), so never surface the row skeleton for it —
                // otherwise a Free member's search/sort refetch flashes fallback shimmer rows in a view
                // specified to have none.
                isLoading={isFree ? false : isLoading}
                isError={isError}
                onSearch={setSearch}
                onSort={setSort}
                onPageChange={setPage}
                onDeleteList={deleteList}
                onOpenList={setSelectedListId}
                onRetry={refetch}
            />
        );
        // Desktop keeps the hub (chrome + rows) above the panel; mobile drops the hub, so a failed
        // refetch surfaces a feed error+retry here instead — shared by the Free and Power-empty panels.
        const hubOrFeedError = isDesktop
            ? hub
            : isError && <HubError data-testid={HUB_ERROR_TESTID} onRetry={refetch} />;
        if (!isPower) {
            // Free (TMS-2169): never-Power members see the hub chrome (column headers + filter, no
            // rows) with the "Upgrade to Power" banner in place of the "Start Creating Lists" one —
            // the same desktop shape as the Power empty state; mobile drops the hub and the preview.
            // A successful upgrade refetches (useSavedPlayerLists) and flips accessStatus to Power,
            // swapping this for the Power empty state + default list.
            if (accessStatus === SavedListsAccessStatus.FREE) {
                return (
                    <>
                        {hubOrFeedError}
                        <SavedListsFreeUpsell data-testid={FREE_UPSELL_TESTID} showPreview={isDesktop} />
                    </>
                );
            }
            // Lapsed / PastDue (TMS-2107): a member who had Power + saved lists but lost access sees the
            // real page-1 lists dimmed/inert behind a centered lock lightbox — GET POWER (Lapsed) enters
            // the upgrade funnel, RESUME POWER (PastDue) reopens the lapsed subscription. Unlike the Free
            // and Power-empty branches (which hide an empty table on mobile), the preserved list data is
            // the loss-aversion signal here, so it stays dimmed behind the lock on Web and Mobile Web. The
            // dimmed hub is display-only (aria-hidden + inert), so a first-load feed failure is handled
            // upstream (accessStatus === null → HubError), not inside this inert region.
            if (accessStatus === SavedListsAccessStatus.LAPSED ||
                accessStatus === SavedListsAccessStatus.PAST_DUE) {
                return (
                    <SavedListsLapsedMessage
                        data-testid={LAPSED_TESTID}
                        status={accessStatus}
                        totalPlayerCount={totalPlayerCount}
                        totalListCount={total}
                        hub={hub}
                    />
                );
            }
            return null;
        }
        if (totalPlayerCount === 0) {
            // Power with no players → the "Start Creating Lists" empty state (TMS-2168). Desktop keeps
            // the single default-row table above the panel + preview (228-23188); mobile goes header →
            // panel only, with no table and no preview illustration (228-22862).
            return (
                <>
                    {hubOrFeedError}
                    <SavedListsEmptyState data-testid={EMPTY_STATE_TESTID} showPreview={isDesktop} />
                </>
            );
        }
        return hub;
    };

    const handleBackToHub = () => {
        // Returning to the hub after a rename or Add Player in detail must show the fresh name/count.
        // useSavedPlayerLists lives here (not in the hub subtree), so unmounting the hub doesn't reset
        // it and nothing else refetches on the way back — do it explicitly.
        setSelectedListId(null);
        refetch();
    };

    const renderHubView = () => (
        <>
            <div className={styles.headerRow}>
                <div className={styles.toggle} data-testid={dataTestId && `${dataTestId}.header.toggle`}>
                    <button
                        type="button"
                        className={`${styles.toggleBtn} ${styles.toggleBtnActive}`}
                        data-testid={dataTestId && `${dataTestId}.header.players-toggle`}
                    >
                        Players
                    </button>
                    <button
                        type="button"
                        disabled
                        className={`${styles.toggleBtn} ${styles.toggleBtnDisabled}`}
                        data-testid={dataTestId && `${dataTestId}.header.events-toggle`}
                    >
                        Events (Coming Soon)
                    </button>
                </div>
                <div className={styles.actions} data-testid={dataTestId && `${dataTestId}.header.actions`}>
                    <button
                        type="button"
                        className={actionClassName}
                        disabled={!isPower}
                        onClick={handleAddNewList}
                        data-testid={dataTestId && `${dataTestId}.header.create-list`}
                    >
                        <i className="far fa-plus-circle" />
                        <span data-testid={dataTestId && `${dataTestId}.header.create-list.label`}>Add New List</span>
                    </button>
                    <span className={styles.divider} />
                    <button
                        type="button"
                        className={actionClassName}
                        disabled={!isPower}
                        onClick={handleAddPlayer}
                        data-testid={dataTestId && `${dataTestId}.header.add-player`}
                    >
                        <i className="far fa-plus-circle" />
                        <span data-testid={dataTestId && `${dataTestId}.header.add-player.label`}>Add Player</span>
                    </button>
                    <span className={styles.divider} />
                    <button
                        type="button"
                        className={actionClassName}
                        disabled={!isPower}
                        onClick={() => setIsNotificationsModalOpen(true)}
                        data-testid={dataTestId && `${dataTestId}.header.notifications`}
                    >
                        <i className="far fa-cog" />
                        <span data-testid={dataTestId && `${dataTestId}.header.notifications.label`}>Notifications</span>
                    </button>
                </div>
            </div>
            {renderBody()}
        </>
    );

    return (
        <div className={styles.savedListsHeaderWrapper} data-testid={dataTestId && `${dataTestId}.header`}>
            <div className="container">
                {selectedListId !== null
                    ? <ListDetail listId={selectedListId} onBack={handleBackToHub} />
                    : renderHubView()}
            </div>
            {isNotificationsModalOpen && (
                <SavedListNotificationsModal onClose={() => setIsNotificationsModalOpen(false)} />
            )}
        </div>
    );
};

SavedListsTabContent.propTypes = {
    'data-testid': PropTypes.string,
};

SavedListsTabContent.defaultProps = {
    'data-testid': null,
};

export default SavedListsTabContent;
